import DOMPurify from "dompurify";

/**
 * The email body is markup now, so everything that touches it has to agree on
 * what that markup is allowed to contain.
 *
 * ---------------------------------------------------------------------------
 * THREE CALLERS, ONE DEFINITION
 * ---------------------------------------------------------------------------
 * The composer's editor produces it, the preview rail renders it, and the
 * payload ships it. If each decided for itself what "safe HTML" meant, the
 * preview would eventually render something the wire never carried, or the wire
 * would carry something the preview never showed. Both are the same bug seen
 * from two ends.
 */

/**
 * What an email body may contain.
 *
 * Deliberately short. This is a broadcast body written in a console text box,
 * not a page: paragraphs, line breaks, emphasis and links. Anything richer
 * would render inconsistently across mail clients anyway.
 */
const EMAIL_ALLOWED_TAGS = [
  "a",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "br",
  "div",
  "p",
  "span",
];

/**
 * `href` for the destination and `style` for the formatting — nothing else.
 *
 * No `target`, no `rel`, and above all no `on*`: an event attribute surviving
 * into the preview would execute inside the console, and one surviving onto the
 * wire would execute in a recipient's client.
 */
const EMAIL_ALLOWED_ATTR = ["href", "style"];

/**
 * The CSS an email body may carry.
 *
 * `style` has to be allowed — the toolbar's bold, colour, highlight and size
 * are inline styles, and a mail client has no stylesheet to fall back on — but
 * "allow style" is a much bigger door than "allow these six declarations".
 * Everything outside this list is dropped by `pruneStyles` below, so a body
 * cannot carry `position`, `content`, or anything else that could reposition or
 * inject in the console.
 */
export const EMAIL_ALLOWED_CSS = [
  "color",
  "background-color",
  "font-size",
  "font-weight",
  "font-style",
  "text-decoration",
  "text-decoration-line",
  "text-underline-offset",
];

/**
 * CSS values that are never legitimate here.
 *
 * `url(` is the one that matters: it is how a style attribute fetches, and a
 * fetch is a beacon even when it cannot execute.
 */
const UNSAFE_CSS_VALUE = /url\(|expression|javascript:|@import|[<>]/i;

/**
 * Rewrite every `style` attribute down to the allowed declarations.
 *
 * Runs on already-sanitized markup, over a detached document — parsing is safe
 * at that point because DOMPurify has removed the tags and attributes that
 * could act during a parse.
 *
 * @param {Document} doc - Parsed, sanitized markup.
 * @returns {void}
 */
const pruneStyles = (doc) => {
  doc.body.querySelectorAll("[style]").forEach((node) => {
    const kept = node
      .getAttribute("style")
      .split(";")
      .map((declaration) => declaration.trim())
      .filter((declaration) => {
        const [property, ...rest] = declaration.split(":");
        const value = rest.join(":");
        if (!property || !value) return false;
        if (!EMAIL_ALLOWED_CSS.includes(property.trim().toLowerCase())) return false;
        return !UNSAFE_CSS_VALUE.test(value);
      });

    if (kept.length) {
      node.setAttribute("style", `${kept.join("; ")}`);
      return;
    }
    node.removeAttribute("style");
  });
};

/** Any flavour of line ending, as a splitter and as a test. */
const NEWLINE_SPLIT = /\r\n|\r|\n/;
const HAS_NEWLINE = /[\r\n]/;

/**
 * Turn newline CHARACTERS into `<br>` ELEMENTS.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS — THE BUG IT FIXES
 * ---------------------------------------------------------------------------
 * `execCommand("insertLineBreak")` does not always insert a `<br>`. Inside a
 * `contentEditable` whose computed `white-space` preserves newlines — which the
 * body editor's box used to do, via `whitespace-pre-wrap` — Chromium inserts a
 * literal `\n` text character instead, and reports success. The editor and the
 * preview rail both rendered that as a line break, because both had the same
 * `pre-wrap`. The email did not: a mail client renders with `white-space:
 * normal`, where `\n` is just whitespace, so every line the author wrote
 * arrived welded into one paragraph.
 *
 * The editor no longer sets `pre-wrap` — that is the actual fix. This is the
 * belt: a newline can still reach the markup by paste, from a browser with its
 * own idea of what `insertLineBreak` means, or from a body composed before this
 * change. Converting at the SANITIZE boundary means the preview and the wire
 * both see the same `<br>`, so what the rail shows stays what the inbox gets.
 *
 * Whitespace-only text nodes are left alone. A newline BETWEEN two elements is
 * formatting in the markup, not a line the author typed, and turning it into a
 * `<br>` would add a blank line nobody asked for.
 *
 * @param {Document} doc - Parsed, sanitized markup.
 * @returns {void}
 */
const breakNewlines = (doc) => {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const targets = [];

  // Collected first, replaced after: mutating the tree mid-walk is how you skip
  // nodes.
  while (walker.nextNode()) {
    const value = walker.currentNode.nodeValue || "";
    if (HAS_NEWLINE.test(value) && value.trim()) targets.push(walker.currentNode);
  }

  targets.forEach((node) => {
    const fragment = doc.createDocumentFragment();
    node.nodeValue.split(NEWLINE_SPLIT).forEach((line, index) => {
      if (index) fragment.appendChild(doc.createElement("br"));
      if (line) fragment.appendChild(doc.createTextNode(line));
    });
    node.replaceWith(fragment);
  });
};

/**
 * The one parse: prune the CSS, promote the newlines.
 *
 * Both passes run over already-sanitized markup in a detached document, and
 * they share the parse because parsing twice to make two small edits is the
 * kind of waste that runs on every keystroke.
 *
 * @param {string} html - Sanitized markup.
 * @returns {string}
 */
const normalize = (html) => {
  if (!html.includes("style") && !HAS_NEWLINE.test(html)) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  pruneStyles(doc);
  breakNewlines(doc);
  return doc.body.innerHTML;
};

/**
 * The inline style put on an inserted anchor.
 *
 * Inline rather than a class because this markup leaves for a mail client,
 * where the console's stylesheet does not exist. `#C24A0C` is `bc-accent-text`.
 */
export const EMAIL_LINK_STYLE =
  "color:#C24A0C;text-decoration:underline;text-underline-offset:2px";

/**
 * Strip everything outside the allowlist from an author-written body.
 *
 * Two passes, because they answer different questions. DOMPurify decides which
 * TAGS and ATTRIBUTES survive — `ALLOWED_URI_REGEXP` is the load-bearing line
 * there, since without it `href` survives the attribute allowlist carrying
 * `javascript:`. `normalize` then decides which CSS survives, which DOMPurify
 * does not police for us once `style` is on the attribute list, and promotes
 * newline characters to `<br>` so a line break means the same thing here, in
 * the preview, and in a mail client.
 *
 * @param {string} html - Raw `innerHTML` from the body editor.
 * @returns {string} Sanitized markup, safe to render and to send.
 */
export const sanitizeEmailHtml = (html) =>
  normalize(
    DOMPurify.sanitize(String(html || ""), {
      ALLOWED_TAGS: EMAIL_ALLOWED_TAGS,
      ALLOWED_ATTR: EMAIL_ALLOWED_ATTR,
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
    })
  );

/** U+00A0, i.e. what `&nbsp;` decodes to. */
const NBSP = / /g;

/**
 * The body as the words in it, with no markup.
 *
 * Two jobs, both of which need the same answer:
 *
 * 1. **Is the body empty?** A `contentEditable` that has been focused and
 *    cleared leaves `<div><br></div>` behind. Trimming the HTML says "not
 *    empty" and lets a blank email through validation; trimming the text says
 *    what the author would say.
 * 2. **What goes in `notification_body`?** That wire field is the inbox preview
 *    line, which is plain text by definition.
 *
 * Parsed rather than regex-stripped so entities decode and `<br>` becomes a
 * space instead of welding two words together.
 *
 * @param {string} html - Body markup.
 * @returns {string} Collapsed, trimmed plain text. `""` for an empty body.
 */
export const emailBodyPlainText = (html) => {
  const source = String(html || "");
  if (!source) return "";

  // Sanitize before parsing. `textContent` never executes anything, but
  // building a live document out of unfiltered markup can still fetch — an
  // `<img src>` aimed at an attacker's host would beacon just by being read.
  const doc = new DOMParser().parseFromString(
    sanitizeEmailHtml(source).replace(/<br\s*\/?>/gi, " "),
    "text/html"
  );

  return (doc.body?.textContent || "")
    .replace(NBSP, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Is there anything in this body?
 *
 * The one question `validateDraft` asks, spelled once so the answer cannot
 * drift from the text extraction above.
 *
 * @param {string} html - Body markup.
 * @returns {boolean}
 */
export const isEmailBodyEmpty = (html) => emailBodyPlainText(html).length === 0;
